import * as React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";

import { useFeatureAccess } from "@/hooks/use-feature-access";
import { useGetAuthenticatedUserQuery } from "@/api/authentication";
import { CopyConversation } from "@/modules/chat/components/CopyConversation";
import { MessageInput } from "@/modules/chat/components/MessageInput";
import { ChatHeader } from "@/modules/chat/components/ChatHeader";
import { usePromptLibraryContext } from "@/modules/promptLibrary/usePromptLibraryContext";
import { useGetChatHistoryQuery } from "@/modules/chat/chat.api";
import { ChatHistory, parseHighlights } from "@/modules/chat/chat.types";
import { useChatWebSocket } from "../hooks/useChatWebSocket";
import { extractArtefacts, extractFilesFromFolder } from "@/modules/chat/chat.utils";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { FileItem, FolderItem } from "@/types/fse";
import { ChatEmptyState } from "../components/ChatEmptyState";
import { ChatFolderEmptyState } from "../components/ChatFolderEmptyState";
import { InterruptDialog } from "../components/InterruptDialog";
import { AgentWarnings } from "../components/AgentWarnings";
import { ChatTranscript } from "../components/ChatTranscript";

interface Props {
  conversationId: string;
  systemId: string;
  fsEntry: FileItem | FolderItem;
  excludeFileIds: string[];
}

const emptyHistory: ChatHistory = {
  evidences: [],
  messages: [],
};

export const ChatInterface: React.FC<Props> = ({ conversationId, fsEntry, systemId, excludeFileIds }) => {
  const { setArtefacts, setSelectedArtefactId } = useCurrentPdf();
  const navigate = useNavigate();
  const { subscribe: subscribeToSelectedPrompt, promptToApply, setPromptToApply } = usePromptLibraryContext();

  const { data: history = emptyHistory, isLoading } = useGetChatHistoryQuery(conversationId, {
    refetchOnMountOrArgChange: true,
  });

  const { hasAccess } = useFeatureAccess();
  const { refetch } = useGetAuthenticatedUserQuery();

  const {
    sendMessage,
    isReady,
    isStreaming,
    messages,
    lastSentMessageId,
    evidences,
    initializeHistory,
    allEvents,
    streamingMessageId,
    cancelStreaming,
    pendingInterrupt,
    resolveInterrupt,
    cancelInterrupt,
    historyTokenWarning,
    largeFolderWarning,
    dismissHistoryTokenWarning,
    dismissLargeFolderWarning,
  } = useChatWebSocket({
    conversationId,
    systemId,
    excludeFileIds,
    onRefetch: refetch,
  });

  React.useEffect(() => {
    if (!isLoading) {
      const mappedMessages = history.messages.map(msg => ({
        ...msg,
        isUser: msg.type === "human",
        timestamp: msg.created_at ? new Date(msg.created_at) : undefined,
        answerSource: msg.answer_source ?? undefined,
      }));

      const mappedEvidences = history.evidences.flat().map(data => ({
        id: data.citation_id,
        citationId: data.citation_id,
        documentId: data.document_id,
        answerOffsetStart: data.answer_start_offset || 0,
        answerOffsetEnd: data.answer_end_offset || 0,
        fseId: data.fse_id,
        highlights: parseHighlights(data.highlights),
      }));

      initializeHistory(mappedMessages, mappedEvidences);
    }
  }, [isLoading, history, initializeHistory]);

  React.useEffect(() => {
    if (!promptToApply || promptToApply.source !== "qa") {
      return;
    }

    if (!isReady) {
      return;
    }

    sendMessage(promptToApply.prompt);
    setPromptToApply(null);
  }, [promptToApply, isReady, sendMessage, setPromptToApply]);

  React.useEffect(() => {
    const unsubscribe = subscribeToSelectedPrompt(prompt => sendMessage(prompt));

    return () => unsubscribe();
  }, [subscribeToSelectedPrompt, sendMessage]);

  const [processedMessages, artefacts] = React.useMemo(() => extractArtefacts(messages), [messages]);

  React.useEffect(() => {
    setArtefacts(artefacts);
  }, [artefacts, setArtefacts]);

  const handleAskQuestion = React.useCallback(
    (file: FileItem) => {
      navigate(`/chat/${file.id}`);
    },
    [navigate],
  );

  return (
    <motion.div
      className="flex h-full w-full flex-col rounded-lg"
      initial={false}
      animate={{ marginLeft: 0 }}
      transition={{ duration: 0.3 }}
    >
      <ChatHeader conversationTitle="Conversation" systemId={systemId} fsEntry={fsEntry} />
      <div className="flex flex-grow flex-col overflow-hidden p-4">
        {hasAccess("system_admin") && (
          <CopyConversation
            evidences={evidences}
            isStreaming={isStreaming}
            messages={processedMessages}
            allEvents={allEvents}
            streamingMessageId={streamingMessageId}
          />
        )}
        <div className="relative flex-grow">
          <ChatTranscript
            messages={processedMessages}
            lastSentMessageId={lastSentMessageId}
            evidences={evidences}
            isStreaming={isStreaming}
            streamingMessageId={streamingMessageId}
            onArtefactClick={setSelectedArtefactId}
            inlineArtefacts={artefacts}
          >
            {!isLoading && processedMessages.length === 0 && fsEntry.type === "file" && (
              <ChatEmptyState name={fsEntry.name} summary={fsEntry.small_summary} />
            )}
            {!isLoading && processedMessages.length === 0 && fsEntry.type === "folder" && (
              <ChatFolderEmptyState files={extractFilesFromFolder(fsEntry)} onAskQuestion={handleAskQuestion} />
            )}
          </ChatTranscript>
        </div>
        <div className="mt-4">
          <AgentWarnings
            historyTokenWarning={historyTokenWarning}
            largeFolderWarning={largeFolderWarning}
            onDismissHistoryToken={dismissHistoryTokenWarning}
            onDismissLargeFolder={dismissLargeFolderWarning}
          />
          <MessageInput
            onSendMessage={sendMessage}
            isStreaming={isStreaming}
            isReady={isReady}
            onCancel={cancelStreaming}
          />
        </div>
      </div>
      <InterruptDialog pendingInterrupt={pendingInterrupt} onResolve={resolveInterrupt} onCancel={cancelInterrupt} />
    </motion.div>
  );
};
